import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  map,
  merge,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { FirestoreConnectorInfo, ChargingStationLocationDetails, ChargingStationLocationDisplay } from '../charging-station-location-model';
import { MatDialog } from '@angular/material/dialog';
import {
  MultipleConnectorPricesPolicyDialogComponent,
  MultipleConnectorPricesPolicyDialogData,
} from '../multiple-connector-prices-policy-dialog/multiple-connector-prices-policy-dialog.component';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroup, AccessGroupSearchEvent } from '../../access-groups/access-group.model';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { ChoosePayterTerminalModalComponent } from '../choose-payter-terminal-modal/choose-payter-terminal-modal.component';
import {
  ChargingStationLocationUpdateAccessGroupsAllConnectorsComponent,
  UpdateAccessGroupsAllConnectorsModalData,
} from '../chargin-station-location-update-access-groups-all-connecctors/charging-station-location-update-access-groups-all-connectors.component';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { RequiresFeatureRoleDirective } from 'src/app/shared/directives/requires-feature-role.directive';
import { RouterLink } from '@angular/router';
import { ConnectorListComponent } from '../connector-list/connector-list.component';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { TranslateLocationCategoryEnum } from 'src/app/shared/pipes/translate-location-category-enum.pipe';
import { TranslateLocationStatusEnum } from 'src/app/shared/pipes/translate-location-status-enum.pipe';
import { TranslateLocationFacilityPipe } from 'src/app/shared/pipes/translate-location-facility.pipe';
import { AuthService } from 'src/app/core/auth/auth.service';
import { TranslateLocationProductPipe } from 'src/app/shared/pipes/translate-location-product-pipe';
import { EmptyDashPipe } from 'src/app/shared/pipes/empty-dash.pipe';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ChargingStationLocationsStateService } from '../charging-station-location-overview/charging-station-locations.state.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ConnectorService } from 'src/app/shared/services/connector.service';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';
import { PriceRegulationService } from 'src/app/shared/services/price-regulation.service';
import { LocationPriceRegulation, PriceRegulationType } from '../../prices/price-regulations/price-regulation';
import { ChargingStation, LocationDetailsConnector } from './charging-station-locations-details.model';
import { ConnectorQueryItemResponse } from 'src/app/shared/services/interfaces/connector.interface';

@Component({
  selector: 'adm-charging-station-location-details',
  templateUrl: './charging-station-location-details.component.html',
  styleUrls: ['./charging-station-location-details.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    RouterLink,
    ConnectorListComponent,
    DecimalPipe,
    NgTemplateOutlet,
    DkCurrencyPipe,
    TranslateLocationCategoryEnum,
    TranslateLocationStatusEnum,
    TranslateLocationFacilityPipe,
    TranslateLocationProductPipe,
    EmptyDashPipe,
    MatButton,
    MatIcon,
  ],
})
export class ChargingStationLocationDetailsComponent {
  private dialog = inject(MatDialog);
  private featureFlagService = inject(FeatureFlagService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private chargingStationLocationService = inject(ChargingStationLocationService);
  private connectorService = inject(ConnectorService);
  private snackbarService = inject(SnackbarService);
  private accessGroupService = inject(AccessGroupService);
  private authService = inject(AuthService);
  private priceRegulationService = inject(PriceRegulationService);
  public stateService = inject(ChargingStationLocationsStateService);

  public roles = this.environmentLoaderService.virtualRoles;
  public location = input.required<ChargingStationLocationDisplay>();
  selectedPricePolicyId = input<number | undefined>();
  destroyRef = inject(DestroyRef);

  public priceRegulations = signal<LocationPriceRegulation[]>([]);
  public locationDetails!: ChargingStationLocationDetails;
  public chargingStations = signal<ChargingStation[]>([]);
  public connectors = computed(() => this.chargingStations().flatMap(station => station.connectors));
  public openPricePolicyUpdateModal$ = signal<boolean>(false);
  public loading = signal<boolean>(false);
  public accessGroups: AccessGroup[] = [];
  public discountType: typeof DiscountType = DiscountType;
  public PriceRegulationType = PriceRegulationType;
  public hasPermissionToManagePayterTerminals: boolean = this.authService.hasRole(this.roles[Permission.PayterPaymentTerminalWrite]);
  public priceRegulationsFeatureEnabled = toSignal(
    this.featureFlagService.getFeatureFlag(FEATURE_FLAGS.PRICE_REGULATIONS_READ).pipe(map(x => x.result)),
    { initialValue: false }
  );

  public powerPriceExclusionFeatureEnabled = signal(false);

  private readonly loadLocationDetailsTrigger$ = new Subject<string>();
  private readonly locationIdentifier$ = toObservable(this.location).pipe(
    map(location => location.identifier),
    distinctUntilChanged()
  );

  private accessgroups$ = (chargingStationLocationIdentifier: string) =>
    this.accessGroupService
      .getAccessGroups(
        (() => {
          const searchEvent = {
            paging: { pageIndex: 0, pageSize: 1000 },
            chargingStationLocationIdentifier: chargingStationLocationIdentifier,
          } as AccessGroupSearchEvent;
          return searchEvent;
        })()
      )
      .pipe(map(res => res.items));

  readonly loadLocationDetails$ = merge(this.locationIdentifier$, this.loadLocationDetailsTrigger$)
    .pipe(
      tap(() => this.loading.set(true)),
      switchMap(chargingStationLocationIdentifier =>
        this.chargingStationLocationService.getChargingStationLocationDetails(chargingStationLocationIdentifier).pipe(
          switchMap(details =>
            forkJoin({
              accessGroups: this.accessgroups$(chargingStationLocationIdentifier),
              connectorsResponse: this.connectorService.getConnectors({
                locationIdentifier: chargingStationLocationIdentifier,
                pageNumber: 0,
                pageSize: 1000,
              }),
              priceRegulations: this.priceRegulationService
                .getLocationPriceRegulations(chargingStationLocationIdentifier)
                .pipe(catchError(() => of([]))),
            }).pipe(
              map(({ accessGroups, connectorsResponse, priceRegulations }) => ({
                details,
                accessGroups,
                connectorItems: connectorsResponse.connectors,
                priceRegulations,
              }))
            )
          ),
          tap(({ details, accessGroups, connectorItems, priceRegulations }) => {
            this.accessGroups = accessGroups;
            this.locationDetails = details;
            this.chargingStations.set(this.groupConnectorsByStation(connectorItems, accessGroups, this.location().restricted));
            this.priceRegulations.set(priceRegulations);
          }),
          catchError(() => {
            this.snackbarService.error('Der skete en fejl under indlæsning af lokationsdetaljer');
            return of(null);
          }),
          finalize(() => this.loading.set(false))
        )
      ),
      takeUntilDestroyed()
    )
    .subscribe();

  constructor() {
    const openPricePolicyUpdateModal = this.openPricePolicyUpdateModal$();
    if (openPricePolicyUpdateModal) {
      this.updateConnectorPrices();
    }
    this.stateService.updateLocationDetails
      .pipe(
        filter(identifier => identifier === this.location().identifier),
        tap(_ => {
          this.refreshAfterUpdate();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    inject(FeatureFlagService)
      .getFeatureFlag(FEATURE_FLAGS.POWER_PRICE_EXCLUSION)
      .pipe(
        takeUntilDestroyed(),
        tap(flagResult => this.powerPriceExclusionFeatureEnabled.set(flagResult.result))
      )
      .subscribe();
  }

  public refreshAfterUpdate() {
    this.loadLocationDetailsTrigger$.next(this.location().identifier);
    this.stateService.updateSingleChargingStationLocation(this.location().identifier);
  }

  public updateConnectorPrices() {
    this.dialog
      .open(MultipleConnectorPricesPolicyDialogComponent, {
        data: {
          chargingStationLocationIdentifier: this.location().identifier,
          canHaveDynamicPricePolicy: this.location().canHaveDynamicPricePolicy,
        } satisfies MultipleConnectorPricesPolicyDialogData,
        disableClose: true,
        width: '640px',
      })
      .afterClosed()
      .subscribe(() => {
        this.refreshAfterUpdate();
      });
  }

  public updateAccessGroupForAllLocations() {
    this.dialog
      .open(ChargingStationLocationUpdateAccessGroupsAllConnectorsComponent, {
        data: <UpdateAccessGroupsAllConnectorsModalData>{
          chargingStationLocationIdentifier: this.location().identifier,
          chargingStationLocationName: this.location.name,
          organizationId: this.location().organizationId,
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe(() => {
        this.refreshAfterUpdate();
      });
  }

  public choosePayterTerminalModal(chargingStationIdentifier: string, organizationId: number) {
    this.dialog
      .open(ChoosePayterTerminalModalComponent, {
        data: {
          chargingStationIdentifier: chargingStationIdentifier,
          organizationId: organizationId,
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe(resp => {
        if (resp) {
          this.refreshAfterUpdate();
        }
      });
  }

  private sortByName(accessGroups: AccessGroup[]) {
    return accessGroups.sort((a, b) => {
      const na = a.name.toLowerCase();
      const nb = b.name.toLowerCase();

      if (na < nb) {
        return -1;
      }
      if (na > nb) {
        return 1;
      }
      return 0;
    });
  }

  private groupConnectorsByStation(
    connectorItems: ConnectorQueryItemResponse[],
    accessGroups: AccessGroup[],
    locationRestricted: boolean
  ): ChargingStation[] {
    const mappedConnectors = connectorItems.map(connectorItem => {
      const connectorAccessGroups = accessGroups.filter(ag => ag.connectorIdentifiers?.includes(connectorItem.identifier));
      const hasMissingPrice = !connectorItem.pricePolicyId;

      return {
        stationIdentifier: connectorItem.stationIdentifier,
        connector: {
          connectorId: connectorItem.connectorId,
          stationNumber: connectorItem.stationNumber,
          identifier: connectorItem.identifier,
          stationIdentifier: connectorItem.stationIdentifier,
          power: connectorItem.power ?? null,
          connectorThumbnail: this.getConnectorThumbnailTimestamped(connectorItem.identifier),
          isThumbnailHidden: false,
          pricePolicyId: connectorItem.pricePolicyId,
          pricePolicyName: connectorItem.pricePolicyName,
          priceType: connectorItem.priceType,
          consumerPriceInclVat: connectorItem.consumerPriceInclVat,
          firestoreConnectorInfo: new BehaviorSubject<FirestoreConnectorInfo>(null as unknown as FirestoreConnectorInfo),
          hasLimitedAccess: false,
          hasMissingPrice,
          hasRestricedAccess: false,
          accessGroups: connectorAccessGroups,
          accessGroupsNames: this.sortByName(connectorAccessGroups)
            .map(ag => ag.name)
            .join(', '),
          terminals: connectorItem.terminals?.map(terminal => ({
            serialNumber: terminal?.serialNumber ?? '',
            type: terminal?.type ?? '',
          })),
          showDangerBadge: false,
          dangerBadgeMessage: '',
        } as LocationDetailsConnector,
      };
    });

    const connectorsWithLimitedAccessCount = mappedConnectors.filter(connector => connector.connector.hasLimitedAccess).length;

    const groupedByStation = mappedConnectors.reduce(
      (grouped, mappedConnector) => {
        const hasRestricedAccess =
          !mappedConnector.connector.hasLimitedAccess &&
          connectorsWithLimitedAccessCount !== 0 &&
          connectorsWithLimitedAccessCount !== mappedConnectors.length;

        const showDangerBadge =
          hasRestricedAccess ||
          mappedConnector.connector.hasMissingPrice ||
          (locationRestricted && mappedConnector.connector.accessGroups.length === 0);

        const connector: LocationDetailsConnector = {
          ...mappedConnector.connector,
          hasRestricedAccess,
          showDangerBadge,
          dangerBadgeMessage: hasRestricedAccess
            ? 'Ladepunkt mangler begrænsning'
            : mappedConnector.connector.hasMissingPrice
              ? 'Dette ladepunkt mangler pris'
              : locationRestricted && mappedConnector.connector.accessGroups.length === 0
                ? 'Ladepunkt mangler adgangsgruppe'
                : '',
        };

        if (!grouped[mappedConnector.stationIdentifier]) {
          grouped[mappedConnector.stationIdentifier] = [];
        }

        grouped[mappedConnector.stationIdentifier].push(connector);
        return grouped;
      },
      {} as Record<string, LocationDetailsConnector[]>
    );

    return Object.entries(groupedByStation).map(([identifier, connectors]) => ({
      identifier,
      connectors,
    }));
  }

  private getConnectorThumbnailTimestamped(connectorIdentifier: string): string {
    return `${
      this.environmentLoaderService.config['serviceBaseUrl']
    }/api/v1/admin/ConnectorPicture/${connectorIdentifier}/thumbnail?${new Date().getTime()}`;
  }
}
